import { useState, useEffect } from 'react';
import { Button, Upload, MessagePlugin } from 'tdesign-react';
import { UploadIcon } from 'tdesign-icons-react';
import { uploadApi } from '../../user/api';

interface ProductImageUploaderProps {
  value?: string[] | string;
  onChange?: (value: string | string[]) => void;
  readOnly?: boolean;
  simple?: boolean; 
}

export default function ProductImageUploader({ value, onChange, readOnly = false, simple = false }: ProductImageUploaderProps) {
  // 非 simple 模式下，内部状态统一维护为 string[]
  const [previewUrl, setPreviewUrl] = useState<string[]>([]);
  // simple 模式下，内部状态维护为 string
  const [simpleUrl, setSimpleUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      if (simple) {
        // 确保 simpleUrl 是字符串
        if (typeof value === 'string') {
          setSimpleUrl(value);
        } else if (Array.isArray(value)) {
          setSimpleUrl(value[0] || '');
        } else {
          setSimpleUrl('');
        }
      } else {
        // 确保 previewUrl 是数组
        if (Array.isArray(value)) {
          setPreviewUrl(value);
        } else if (typeof value === 'string') {
          // 如果是字符串，需要按逗号分割成数组
          // 过滤掉空字符串，防止出现空图片
          const urls = value.split(',').map(u => u.trim()).filter(u => u.length > 0);
          setPreviewUrl(urls);
        } else {
          setPreviewUrl([]);
        }
      }
    } else {
      // 处理 value 为 null 或 undefined 的情况
      if (simple) {
        setSimpleUrl('');
      } else {
        setPreviewUrl([]);
      }
    }
  }, [value, simple]);

  // 处理单个文件上传的核心逻辑
  const uploadSingleFile = async (file: File): Promise<string | null> => {
    if (file.size > 5 * 1024 * 1024) {
      MessagePlugin.error(`图片 ${file.name} 大小不能超过5MB`);
      return null;
    }
    try {
      const url = await uploadApi.uploadImage(file);
      console.log('上传返回的URL:', url);
      if (url) {
        return url;
      } else {
        MessagePlugin.error(`图片 ${file.name} 上传失败`);
        return null;
      }
    } catch (err) {
      MessagePlugin.error(`图片 ${file.name} 上传出错`);
      console.error(err);
      return null;
    }
  };

  const handleMultiUpload = async (files: File[]) => {
    if (files.length === 0) return;
    
    setUploading(true);
    const newUrls: string[] = [];
    
    // 使用 Promise.all 并发上传，或者使用 for...of 串行上传
    // 这里使用并发上传以提高速度
    const uploadPromises = files.map(file => uploadSingleFile(file));
    const results = await Promise.all(uploadPromises);
    
    // 过滤掉上传失败的 null 值
    results.forEach(url => {
      if (url) newUrls.push(url);
    });

    console.log('上传成功的newUrls:', newUrls,'uploadPromises',uploadPromises);
    if (newUrls.length > 0) {
      if (simple) {
        const firstUrl = newUrls[0];
        setSimpleUrl(firstUrl);
        onChange?.(firstUrl);
        MessagePlugin.success('图片上传成功');
      } else {
        const updatedUrls = [...previewUrl, ...newUrls];
        setPreviewUrl(updatedUrls);
        onChange?.(updatedUrls);
        MessagePlugin.success(`成功上传 ${newUrls.length} 张图片`);
      }
    }
    
    setUploading(false);
  };

  const customUpload = (file: File | File[]) => {
    const filesToUpload = Array.isArray(file) ? file : [file];
    // 异步执行，不阻塞 Upload 组件的内部状态
    handleMultiUpload(filesToUpload);
    return Promise.resolve();
  };

  // Simple 模式渲染
  if (simple) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Upload
            theme="custom"
            accept="image/*"
            autoUpload={false}
            draggable
            multiple={false} // Simple 模式通常只允许单张
            onSelectChange={(files: File[]) => {
              if (files.length > 0 && !readOnly) {
                customUpload(files[0]);
              }
            }}
          >
             <div className={`border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-primary transition-colors ${uploading || readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className="flex flex-col items-center justify-center text-gray-500">
                  <UploadIcon className="w-6 h-6 mb-2" />
                  <span className="text-sm">{uploading ? '上传中...' : '点击或拖拽上传'}</span>
                </div>
             </div>
          </Upload>
        </div>

        {simpleUrl && (
          <div className="mt-3 p-2 border rounded bg-gray-50 relative group inline-block">
            <img
                src={simpleUrl}
                alt="预览"
                className="max-h-40 max-w-full object-contain rounded block"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  MessagePlugin.error('图片预览加载失败');
                }}
              />
              {!readOnly && (
                <button 
                  onClick={() => { 
                    setSimpleUrl(''); 
                    onChange?.(''); 
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
          </div>
        )}
      </div>
    );
  }

  // 原有逻辑（多图模式）
  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="flex items-center gap-3">
          <Upload
            theme="custom"
            accept="image/*"
            autoUpload={false}
            draggable
            multiple // 启用多选
            onSelectChange={(files: File[]) => {
              if (files.length > 0) {
                customUpload(files);
              }
            }}
          >
            <div className={`border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-primary transition-colors ${uploading || readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className="flex flex-col items-center justify-center text-gray-500">
                  <UploadIcon className="w-6 h-6 mb-2" />
                  <span className="text-sm">{uploading ? '上传中...' : '点击或拖拽上传多张图片'}</span>
                </div>
             </div>
          </Upload>
          {previewUrl.length > 0 && (
            <span className="text-sm text-gray-500">已上传 {previewUrl.length} 张</span>
          )}
        </div>
      )}

      {/* 修复点：previewUrl 现在保证是 string[]，可以直接 map */}
      {previewUrl.length > 0 && (
        <div className="mt-3 p-2 border rounded bg-gray-50 flex flex-wrap gap-2">
          {previewUrl.map((url, index) => (
            <div key={index} className="relative group inline-block">
              <img
                src={url}
                alt={`封面预览-${index}`}
                className="max-h-40 max-w-full object-contain rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  MessagePlugin.error('图片预览加载失败');
                }}
              />
              {!readOnly && (
                 <button 
                 onClick={() => {
                   const newUrls = previewUrl.filter((_, i) => i !== index);
                   setPreviewUrl(newUrls);
                   onChange?.(newUrls);
                 }}
                 className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
               >
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}